// =============================================================================
//  Copyright : 2019 semantics GmbH
//  Date      : $Date: 2026-01-14 17:43:36 +0100 (Wed, 14 Jan 2026) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 159808 $
//  Purpose   : uix root react-hooks
// =============================================================================
// ---Third-party imports
import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
  useLayoutEffect as react_useLayoutEffect,
  useDebugValue,
  createContext,
  useContext,
} from 'react'
import axios from 'axios'

import {useDebounce, useThrottledCallback} from 'use-debounce'
import {useInView} from 'react-intersection-observer'

// ---Framework imports
import {Actions, Sel} from 'uix/registries'
import {
  get,
  dict,
  mapItems,
  values,
  shallowEqual,
  isNil,
  has,
  isArrayEqual,
  isString,
  isArray,
  flatten,
  zip,
} from 'ldash'
import log from 'uix/logging'
import {Graphics} from 'uix/graphics'
import {hashObj, isSSR} from 'uix/utils'

import {buildAncestorAxis} from 'uix/selectors'
import {normalize} from 'uix/normalize'

// ---Local imports
import {useSelector, useDispatch, useActions} from './redux'
import {useChunk} from './chunks'

// ---Import/Exports
export {useReducer} from 'react'
export {useGraphics} from './chunks'
export {
  usePersistent,
  usePersistentShallow,
  usePersistentWithQueryOverride,
  useUserSetting,
} from 'uix/persist/ui'

export {useSummaries, useSummary, useTimestats} from './stats'
import {PartContext, usePartContext} from 'uix/parts/hooks'

let global_hooks = {}
// reset if SSR renders new view (but not if only action is dispatched)
export const resetSSREffects = () => (global_hooks = {})

const _useEffectSSR = (cb, deps) => {
  /* simulate useEffect, run once for unchanged deps for each stack location
  XXX: how to make this component local (instead of stack local)?
  */
  let stack = new Error().stack.split('\n').map(e =>
    e
      .replaceAll('\\', '/')
      .split('webpack:')
      .at(-1)
  )
  // also filter "renderToString(App)" call in tests/server.js
  let filtered = ['Error:', '/react-dom/', 'tests/server.js:']
  stack = stack.filter(e => !filtered.some(f => e.includes(f))).join(' ')

  // getting older dependencies from the hooks array since
  // we are storing dependencies as a sub-array inside the hooks array
  let oldDeps = global_hooks[stack]

  // if no dependencies are provided,
  // the callback function will be called at each re-render
  let hasChanged = true

  if (oldDeps) {
    // checking if the old dependencies are different from older dependencies
    hasChanged = deps.some((d, index) => !Object.is(d, oldDeps[index]))
  }
  if (hasChanged) cb() // if dependencies has changed call the callback function.

  global_hooks[stack] = deps //store dependencies inside the hooks array as a sub-array
}

// in SSR:
// useEffectSSR will be executed once for each stack location (and unchanged deps)
// useEffect will *never* be executed
const useEffectSSR = isSSR ? _useEffectSSR : useEffect
// useLayoutEffect will *never* be executed
// see https://gist.github.com/gaearon/e7d97cdf38a2907924ea12e4ebdf3c85
const useLayoutEffect = isSSR ? f => null : react_useLayoutEffect

// ---Exports
export {
  useEffect,
  useEffectSSR,
  useLayoutEffect,
  useState,
  useRef,
  useMemo,
  useDispatch,
  useDebounce,
  useThrottledCallback,
  useActions,
  useSelector,
  useInView, // isVisible
  useCallback,
  PartContext,
  usePartContext,
  useChunk,
}

const getCancelSource = () => axios.CancelToken.source()

// selector should return a dict, only rerender if dict values change
export const useSelectorShallow = selector => useSelector(selector, shallowEqual)

export const useResettableState = (initialiser, deps) => {
  /* like useState, but reverts to initialValue on deps change
  see https://github.com/facebook/react/issues/14738#issuecomment-771292736
  */
  let internals = (useRef().current ||= {
    value: initialiser instanceof Function ? initialiser(...deps) : initialiser,
    deps,
    setState: value => {
      // in react value can also be a function that receives
      // the prev state and calcs next state
      internals.value = value instanceof Function ? value(internals.value) : value
      return setState(value)
    },
  })
  let setState = useState(internals.value)[1]

  let needStateReset = !isArrayEqual(deps, internals.deps)
  // Refresh value if the deps change.
  if (needStateReset) {
    internals.value = initialiser instanceof Function ? initialiser(...deps) : initialiser
    internals.deps = deps
  }
  // this fixes "going back to previous value after reset"
  useEffect(() => {
    if (needStateReset) {
      setState(internals.value)
    }
  }, [needStateReset])

  return [internals.value, internals.setState]
}

export const useEffectExceptOnMount = (func, deps) => {
  /* like useEffect but do *not* run on initial render
  This is the equivalent of the componentDidUpdate lifecycle function.
  */
  const didMount = useRef(false)

  useEffect(() => {
    if (didMount.current) {
      const unmount = func()
      return () => {
        didMount.current = false
        unmount && unmount()
      }
    } else didMount.current = true
  }, deps)
}

const useApiPartsReload = () => {
  /* connect useApi to reloadPartFilters action
  each reloadPartFilters call increases the counter, data is resetted on activateView action
   */
  let {name: part_name, type: part_type} = usePartContext()
  return useSelector(root => root.data.apiPartsReload[part_name || part_type]) || 0
}

const API_RESULT_LOADING = {data: null, error: null, loading: true, reload: () => null}
const API_RESULT_DISABLED = {...API_RESULT_LOADING, loading: false}
export const useApi = (name, params, opts = {}) => {
  /* use server http api via hooks (using getApi redux action)
  - opts.condition: if false, do not execute
  - opts.conditionNullResult: if false, emit old data
  - opts.deps: changes on deps triggers refetch
  - opts.cached: if true, reuse results of previous usage, no new call (survives unmount but not reload)
  - opts.cacheKey: more keys for cache, default keys are derived from params + opts
  - opts.autoreset: if set to false: previous results are returned also *during* next call
  - opts.partReloadable: if set to true, call will be reexecuted if part is reloaded (reloadPartFilters)
  - opts.disableErrorNotify: do not show error-toast if server responds with an error (apiopts pass through)
  - other opts are passed through as apiopts
   */

  // 'useApi should only be used with api names (not with routes'
  if (!opts.raw && name.startsWith('/')) throw Error(`invalid api name ${name}`)

  let partReloadIndex = useApiPartsReload()

  let [getApi, clearApiResult] = useActions(['getApi', 'clearApiResult'])
  let dname = useSelector(root => root.meta.request.dname)
  // a opts.condition with undefined value should be understood as enabled = false
  // so we use an explicit has() call here
  let enabled = !has(opts, 'condition') || Boolean(opts.condition)
  let {
    condition,
    conditionNullResult = true,
    deps,
    cached,
    cacheKey,
    autoreset = true,
    partReloadable = false,
    ...apiopts
  } = opts
  let [reloadIdx, setReloadIdx] = useState(0)
  let reload = useCallback(() => setReloadIdx(reloadIdx + 1), [reloadIdx, setReloadIdx])

  // if autoDomain in apiopts add dname to key
  let key = hashObj([
    name,
    opts.autoDomain !== false ? dname : null,
    params,
    deps,
    apiopts,
    cacheKey,
    reloadIdx + (partReloadable ? partReloadIndex : 0),
  ])
  let prev_key = usePrevious(key)

  useEffect(() => {
    // cleanup stored api results
    if (prev_key) {
      clearApiResult(prev_key)
    }
    return () => clearApiResult(key)
  }, [key, prev_key])

  let result = useSelector(root => root.data.apiResult[key])

  let [apiId, setApiId] = useState(null)

  // keep and emit prevResult if autoreset=false
  // prevResult can have other other parametrization (a opts.cached result can not)
  let [prevResult, setPrevResult] = useState(null)
  useEffect(() => {
    if (!autoreset && result) {
      // a prev result is always "loading"
      setPrevResult({...result, loading: true})
    }
  }, [result])

  // SSR support
  let _useEffect = useEffect
  if (isSSR) {
    // go into cached mode for accepting result
    cached = true
    // run once if enabled and no result
    if (enabled && !result && !apiId) {
      _useEffect = useEffectSSR
    }
  }

  _useEffect(() => {
    let cancelSource = getCancelSource() // axios based request cancelling
    let do_call = enabled && (!result || !cached)
    if (do_call) {
      let action = getApi(name, params, key, {
        ...apiopts,
        cancelToken: cancelSource.token,
      })
      setApiId(action.meta.apiId)
    }

    return () => {
      if (do_call) {
        try {
          cancelSource.cancel(`${name} [unmount]`)
        } catch (e) {
          // seen: Cannot add property reason, object is not extensible
          log.warn(`error during cancel ${name}`)
        }
      }
    }
  }, [key, enabled])

  if (!cached && result?.apiId !== apiId) {
    result = null // reject result
  }
  // a non-nil result has always loading: true
  result = useMemo(() => (isNil(result) ? result : {...result, reload, loading: false}), [
    result,
    reload,
  ])
  let res = result || prevResult || API_RESULT_LOADING // return {data, error, loading}
  useDebugValue(res)

  // if not enabled (and survives conditionNullResult check), set loading
  // to false as prevResult.loading is always true
  res = useMemo(() => (enabled ? res : {...res, loading: false}), [res, enabled])

  if (!enabled && conditionNullResult) {
    return API_RESULT_DISABLED
  }

  return res
}

export const useCallApi = (name, options) => {
  /* use callApi with activity tracking. Usage:
  let {callApi} = useCallApi('myApi')
  callApi(params, callback)
  */

  let callApi = useActions(Actions.callApi)
  let [calling, setCalling] = useState(false)
  let _callApi = useCallback(
    (params, callback, more_options = {}) => {
      let cb = (data, error) => {
        setCalling(false)
        if (callback) {
          callback(data, error)
        }
      }
      setCalling(true)
      callApi(name, params, cb, {method: 'post', ...options, ...more_options})
    },
    [name]
  )
  return {callApi: _callApi, calling}
}

export const useDownload = (path, params, opts = {}) => {
  /* returns {data, error} for fetch call
  data is normalized to the raw response content (string)
  Attention:
    - if @auth_required is active add allowOrigin=True
    - if not auth_required is active, use vl.spa.allowOrigin
  */

  let res = useApi(path, params, {...opts, raw: true})

  return useMemo(() => ({...res, data: res.data?.responseText ?? null}), [res])
}

export const useRouter = () => {
  /* read routing state and navigate (forward) with go
   */
  let [go, replaceQueryParams] = useActions(['go', 'replaceQueryParams'])
  let request = useSelector(root => root.meta.request)
  let apiurl = useSelector(Sel.apiurl)
  // reload all parts
  let refresh = useMemo(() => () => go({}), [go])
  return {request, go, refresh, replaceQueryParams, apiurl}
}

export const useImagePreload = urls => {
  /* preload urls and return dict with key: url and value: load failed/success
   */
  let [loaded, setLoaded] = useState({})
  useEffect(() => {
    urls.forEach(url => {
      const img = new Image()
      img.onload = () => (loaded[url] !== undefined ? null : setLoaded({...loaded, [url]: true}))
      img.onerror = () => (loaded[url] !== undefined ? null : setLoaded({...loaded, [url]: false}))
      img.src = url
    })
  }, [urls.join(',')])

  return loaded
}

// Hook
export function useWhyDidYouUpdate(name, props) {
  /*
  https://usehooks.com/useWhyDidYouUpdate/
  */

  // Get a mutable ref object where we can store props ...
  // ... for comparison next time this hook runs.
  const previousProps = useRef()

  useEffect(() => {
    if (previousProps.current) {
      // Get all keys from previous and current props
      const allKeys = Object.keys({...previousProps.current, ...props})
      // Use this object to keep track of changed props
      const changesObj = {}
      // Iterate through keys
      allKeys.forEach(key => {
        // If previous is different from current
        if (previousProps.current[key] !== props[key]) {
          // Add to changesObj
          changesObj[key] = {
            from: previousProps.current[key],
            to: props[key],
          }
        }
      })

      // If changesObj not empty then output to console
      if (Object.keys(changesObj).length) {
        console.log('[why-did-you-update]', name, changesObj)
      }
    }

    // Finally update previousProps with current props for next hook call
    previousProps.current = props
  })
}

export const useToggle = (initialState = false) => {
  const [state, setState] = useState(initialState)
  const toggle = useCallback(() => setState(state => !state), [])
  return [state, toggle]
}

export const usePrevious = (value, initialDefault, filterUpdates) => {
  /* https://reactjs.org/docs/hooks-faq.html#how-to-get-the-previous-props-or-state

  filterUpdates: value => true/false
   */

  let ref = useRef()

  // Store current value in ref
  useEffect(() => {
    if (!filterUpdates || filterUpdates(value)) {
      ref.current = value
    }
  }, [value]) // Only re-run if value changes

  // if result should be something else than undefined in initial run
  if (ref.current === undefined) return initialDefault

  // Return previous value (happens before update in useEffect above)
  return ref.current
}

export const useInterval = (callback, delay, ...deps) => {
  // Ref: https://overreacted.io/making-setinterval-declarative-with-react-hooks/
  /**
   * Params
   * @param {function} callback - Custom logic function
   * @param {number|null} delay - Delayed millisecond, stop if null
   reset interval on mutated deps
  */
  if (delay === 0) {
    throw Error('should be > 0')
  }
  // Remember the latest callback.
  const savedCallback = useRef()
  useEffect(() => {
    savedCallback.current = callback
  })

  // Set up the interval.
  useEffect(() => {
    function tick() {
      savedCallback.current()
    }
    if (delay !== null) {
      const id = setInterval(tick, delay)
      return () => clearInterval(id)
    }
  }, [delay, ...deps])
}

export const useDelay = (callback, delay, deps = []) => {
  /* run callback delayed once after deps change
  - execute immediately if delay === 0
  - disable if delay === null
   */
  let disabled = delay === null
  let immediate = delay === 0

  let [triggered, setTriggered] = useState(false)
  // reset triggered if deps change
  useEffect(() => setTriggered(false), [...deps, disabled])
  // Remember the latest callback.
  const savedCallback = useRef()
  useEffect(() => {
    savedCallback.current = callback
  })

  let trigger = () => {
    if (!triggered) {
      setTriggered(true)
      // run callback
      savedCallback.current()
    }
  }
  //~ log.x(delay, inactive)
  // set null if condition failed
  useInterval(trigger, triggered || immediate || disabled ? null : delay, ...deps)
  // if delay === 0, execute immediately
  useEffect(() => {
    if (immediate && !disabled) {
      trigger()
    }
  }, [immediate, triggered, disabled, ...deps])
}

export const useIsOffline = () => useSelector(Sel.isOffline)

export const usePhysicalNode = (resolveLink = true) => {
  /* get current node from unbound ancestorAxis filter (request id)
  XXX: rename to useRequestNode
   */
  let node = useSelector(Sel.currentNode)
  return useMemo(
    () =>
      resolveLink && node.otlink
        ? {link_otid: node.otid, ...node, otid: node.otlink, odid: node.odlink}
        : node,
    [node, resolveLink]
  )
}

export const FullscreenContext = createContext({isFullscreen: false, ref: null})
export const useFullscreenContext = () => {
  let {isFullscreen, ref} = useContext(FullscreenContext)
  return {isFullscreen, ref, domNode: isFullscreen && ref?.current ? ref.current : null}
}

export const useFullscreen = externalRef => {
  /* manage fullsceen state of a dom node
   */
  // see: https://caniuse.com/?search=requestFullscreen
  let ref = externalRef || useRef()
  // we also ignore safari here (needs webkit prefix)
  if (isSSR || document.fullscreenElement === undefined) return {ref, toggleFullscreen: () => {}}
  let [isFullscreen, setIsFullscreen] = useState(document.fullscreenElement !== null)

  // ensure fullscreen states of other elements do not
  if (document.fullscreenElement !== ref.current && isFullscreen) {
    isFullscreen = false
  }

  let toggleFullscreen = useCallback(() => {
    if (ref.current) {
      if (!isFullscreen) {
        ref.current.requestFullscreen()
      } else {
        document.exitFullscreen()
      }
    }
  }, [ref, isFullscreen])

  useLayoutEffect(() => {
    let onFullscreenChange = () => setIsFullscreen(document.fullscreenElement === ref.current)
    document.addEventListener('fullscreenchange', onFullscreenChange)
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange)
  }, [])
  return {ref, toggleFullscreen, isFullscreen}
}

// copied from sui/Responsive
const RESPOND = [
  {name: 'onlySubMobile', minWidth: 0, maxWidth: 319},
  {name: 'onlyMobile', minWidth: 320, maxWidth: 767},
  {name: 'onlyTablet', minWidth: 768, maxWidth: 991},
  {name: 'onlyComputer', minWidth: 992, maxWidth: 999999},
  {name: 'onlyLargeScreen', minWidth: 1200, maxWidth: 1919},
  {name: 'onlyWidescreen', minWidth: 1920, maxWidth: 999999},
]

export const useResponsive = deltaWidth => {
  /* if deltaWidth is given, trigger if deltaWidth is invalidated addtionally to
  breakpoint name changes
  */
  let [respondMode, setRespondedMode] = useState({names: ['onlyComputer'], width: 1000})

  let onResize = ({width}) => {
    let names = RESPOND.filter(e => width > e.minWidth && width < e.maxWidth).map(e => e.name)

    if (
      respondMode.names.slice(-1)[0] !== names.slice(-1)[0] ||
      (deltaWidth && Math.abs(respondMode.width - width) > deltaWidth)
    ) {
      setRespondedMode({names, width})
    }
  }
  let ref = useResizeObserver({onResize})
  return [respondMode.names, ref, respondMode.width]
}

export const useResizeObserver = ({ref, onResize, types = []}) => {
  ref = ref || useRef() //React.createRef()
  // disable in SSR
  if (isSSR) return ref

  let observer = null

  let [refAvailable, setRefAvailable] = useState(false)

  useLayoutEffect(() => {
    observer = new ResizeObserver(entries => {
      if (!isNil(ref.current)) {
        const contentRect = types.length > 0 ? getContentRect(ref.current, types) : {}
        let cr = entries[0].contentRect
        onResize({...contentRect, width: cr.width, height: cr.height})
      }
    })

    if (!isNil(ref.current)) {
      observer.observe(ref.current)
    }

    return () => {
      observer !== null && observer.disconnect()
    }
  }, [onResize, hashObj(types), refAvailable])

  // this looks ugly
  useEffect(() => {
    if (observer === null && ref.current && !refAvailable) {
      setRefAvailable(true)
    }
  })

  return ref
}

export const useDimensions = ({ref, delta, defaults = undefined} = {}) => {
  /* If delta is given, trigger only if width/height diff since last update > delta
  Note: delta=0 triggers on every actual width/height change
   */
  if (defaults === undefined) {
    defaults = {width: 1600, height: 900}
  } else if (defaults === null) {
    defaults = {width: null, height: null}
  }
  let [dimensions, setDimensions] = useState(defaults)

  let onResize = useCallback(
    ({width, height}) => {
      if (
        !delta ||
        Math.abs(dimensions.width - width) > delta ||
        Math.abs(dimensions.height - height) > delta
      ) {
        setDimensions({width, height})
      }
    },
    [dimensions.width, dimensions.height, delta]
  )
  ref = useResizeObserver({ref, onResize})
  return [dimensions, ref]
}

export const useOverflowDetection = () => {
  /* detect active overflow (only on y-axis atm)
   */
  let [overflowActive, setOverflowActive] = useState(false)

  let onResize = useCallback(
    ({offset, scroll}) => {
      let active = offset.height < scroll.height
      if (active !== overflowActive) {
        setOverflowActive(active)
      }
    },
    [overflowActive]
  )
  let ref = useResizeObserver({onResize, types: ['scroll', 'offset']})
  return [overflowActive, ref]
}

export const useBoundingclientrect = ({
  topDelta = null,
  leftDelta = null,
  badPollIntervall = null,
}) => {
  let [bounds, setBounds] = useState({top: 0, left: 0, right: 0, bottom: 0, width: 0, height: 0})
  let [i, seti] = useState(0)

  useInterval(() => seti(i + 1), badPollIntervall)
  let onResize = useCallback(
    ({bounds: nextBounds}) => {
      let topInvalid = topDelta && Math.abs(bounds.top - nextBounds.top) > topDelta
      let leftInvalid = leftDelta && Math.abs(bounds.left - nextBounds.left) > leftDelta
      if (topInvalid || leftInvalid) {
        setBounds(nextBounds)
      }
    },
    [bounds, i]
  )

  let ref = useResizeObserver({onResize, types: ['bounds']})
  return [bounds, ref]
}

function getContentRect(element, types) {
  const calculations = {}
  if (types.indexOf('client') > -1) {
    calculations.client = {
      top: element.clientTop,
      left: element.clientLeft,
      width: element.clientWidth,
      height: element.clientHeight,
    }
  }

  if (types.indexOf('offset') > -1) {
    calculations.offset = {
      top: element.offsetTop,
      left: element.offsetLeft,
      width: element.offsetWidth,
      height: element.offsetHeight,
    }
  }

  if (types.indexOf('scroll') > -1) {
    calculations.scroll = {
      top: element.scrollTop,
      left: element.scrollLeft,
      width: element.scrollWidth,
      height: element.scrollHeight,
    }
  }

  if (types.indexOf('bounds') > -1) {
    const rect = element.getBoundingClientRect()
    calculations.bounds = {
      top: rect.top,
      right: rect.right,
      bottom: rect.bottom,
      left: rect.left,
      width: rect.width,
      height: rect.height,
    }
  }

  if (types.indexOf('margin') > -1) {
    const styles = getComputedStyle(element)
    calculations.margin = {
      top: styles && styles.marginTop ? parseInt(styles.marginTop) : 0,
      right: styles && styles.marginRight ? parseInt(styles.marginRight) : 0,
      bottom: styles && styles.marginBottom ? parseInt(styles.marginBottom) : 0,
      left: styles && styles.marginLeft ? parseInt(styles.marginLeft) : 0,
    }
  }

  return calculations
}

export const useFilter = (name, params, condition) => {
  /* use server /filter http api via hooks
    like runLazyFilter() in sagas
    XXX: experimental - use only if history implications are clearer
   */
  let ax_opts = {}

  let {name: part_name, type: part_type} = usePartContext()
  part_name = part_name || part_type
  let filterData = useSelector(root => get(root.data.parts, `${part_name}.${name}.data`))

  let runId = useSelector(Sel.runId)
  let [callApi, setFilterData] = useActions([Actions.callApi, Actions.setFilterData])
  let [resultKey, setResultKey] = useState(null)
  let key = hashObj([name, params, ax_opts])

  let enabled = !condition || condition()
  useEffect(() => {
    if (!filterData && enabled && resultKey !== key) {
      callApi(`filter/${name}`, params, (data, error) => {
        setResultKey(key)
        if (data) {
          let fd = {[part_name]: {[name]: data}}
          setFilterData({filterData: fd, runId})
        } else {
          log.error('error during filter run')
        }
      })
    }
  }, [key, enabled])

  if (filterData) {
    return {data: {...filterData}, error: null}
  } else {
    return {data: null, error: null}
  }
}
const EMPTY_LIST = []
const useServerEvent = eventName => {
  let user = useSelector(Sel.user)
  let subscribed = useSelector(root => root.data.serverEvents.subscribed[eventName])
  let events = useSelector(root => root.data.serverEvents.events[eventName] || EMPTY_LIST)
  let subscribeServerEvent = useActions(Actions.subscribeServerEvent)
  useEffect(() => {
    if (!subscribed && !user.isGuest) subscribeServerEvent(eventName, true)
    // unsubscribe
    // XXX: on multiple subscriptions, this is broken
    // added module.hot check after switch to react-refresh
    // but only seen sporadically
    return () => (module.hot || user.isGuest ? null : subscribeServerEvent(eventName, false))
  }, [user])
  return events
}

export const useServerEvents = eventNames => {
  /* register to one or multiple events at once
  emit flat list if only one event given or dict with eventNames
   */
  eventNames = isString(eventNames) ? [eventNames] : eventNames
  let events = eventNames.map(useServerEvent)
  if (eventNames.length == 1) {
    // emit flat list
    return useMemo(() => events[0], [events[0]])
  } else {
    // emit dictionary (lists for each eventName)
    return useMemo(() => dict(zip(eventNames, events)), [...events])
  }
}

export const useRecordDataChanged = ({otid, reason = null}) => {
  /* assume subscribed ERecordDataChanged (see subscribeRecordChangedEvents saga + user authenticated user)
   */
  let events = useSelector(root => root.data.serverEvents.events.ERecordDataChanged || EMPTY_LIST)
  // this looks costly (although events is sliced down)
  // ERecordDataChanged could be indexed early on data receive
  events = useMemo(
    () =>
      events.filter(
        e =>
          (!reason ||
            (isString(reason) && e.reason == reason) ||
            (isArray(reason) && reason.includes(e.reason))) &&
          e.otids.includes(otid)
      ),
    [otid, hashObj(reason), events]
  )
  return events
}

// use dconfig.templating[name]
export const useFrontendConfig = name => get(useSelector(Sel.dconfig).templating, name)

export const useVlmState = (name, value) => {
  let setToVlmState = useActions(Actions.setToVlmState)
  useEffect(() => {
    // null means: no value should be send to vlm
    if (value !== null) {
      setToVlmState(name, value)
    }
  }, [name, hashObj(value)])
  return useSelector(root => root.data.vlm.from[name])
}

export const useNotify = () => {
  let [success, warning, error] = useActions([
    'successNotification',
    'warningNotification',
    'errorNotification',
  ])
  return {success, warning, error}
}

export const useAncestorAxis = ({otid, enabled = true}) => {
  let reqTypes = useSelector(Sel.types)
  let i18n = useSelector(Sel.i18n)
  let itypes = useSelector(Sel.itypes)
  let domains = useSelector(Sel.domains)
  let {data} = useApi('filter/ancestorAxisJson', {id: otid}, {condition: !!enabled})

  return useMemo(() => {
    let aaxis = normalize(data?.ancestorAxis || [])
    return buildAncestorAxis(aaxis, reqTypes, i18n, itypes, domains)
  }, [data, reqTypes, i18n, itypes, domains])
}
